<template>
    <div class="pt-3">
        <v-expansion-panels>
            <v-expansion-panel>
                <v-expansion-panel-header>Modify API request parameters</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-form>
                        <div class="mb-4">
                            <v-text-field name="apikey" outlined  label="Your API key" dense v-model="apikey" hide-details @input="validateKey">
                                <template #append-outer>
                                    <v-progress-circular v-if="validatingKey" color="lightgray" size="24" indeterminate
                                                         :aria-label="$t('app.loading')"></v-progress-circular>
                                    <v-icon v-else :color="keyvalid ? 'green' : 'red'" >
                                        {{ apikeyValidIcon }}
                                    </v-icon>
                                </template>
                            </v-text-field>
                        </div>
                        <div class="mb-2">
                            <v-text-field name="text" outlined label="Text for QR code" dense v-model="text"
                            hint="For URLs always include http:// or https://" persistent-hint></v-text-field>
                        </div>
                        <div class="mb-4">
                            <v-select outlined v-model="format" :items="formats" label="Format" dense hide-details></v-select>
                        </div>
                        <div class="mb-4">
                            <v-select outlined v-model="size" :items="sizes" label="Size" dense hide-details></v-select>
                        </div>
                        <v-row v-if="size==='custom'">
                            <v-col class="pl-0 py-0">
                                <v-text-field outlined label="Size (number)" dense v-model="sizenum" type="number" :step="0.01" hide-details></v-text-field>
                            </v-col>
                            <v-col class="py-0">
                                <v-select outlined label="Size (units)" dense v-model="sizeunit" :items="units" hide-details></v-select>
                            </v-col>
                            <v-col class="pr-0 py-0">
                                <v-text-field outlined label="DPI" dense v-model="dpi" type="number" hide-details></v-text-field>
                            </v-col>
                        </v-row>
                        <div v-if="keyvalid">
                            <p class="keyvalid"><small>Your API key is valid.
                                We have performed a request to
                                <a :href="summaryUrl" target="_blank" class="smfm-externallink smfm-externallink-small" @click="openWarningDialog2">/summary</a>
                                to retrieve the list of
                                presets, templates and logos available in your account.
                            </small></p>
                            <div class="mb-4">
                                <v-select outlined v-model="preset" :items="userPresets" label="Preset" dense hide-details
                                        @change="presetUpdated"></v-select>
                            </div>
                            <div class="mb-4">
                                <v-select outlined v-model="template" :items="userTemplates" label="Template" dense hide-details
                                          @change="templateUpdated"></v-select>
                            </div>
                            <div class="mb-4">
                                <v-select outlined v-model="logo" :items="userLogos" label="Logo" dense hide-details></v-select>
                            </div>
                            <v-row class="mb-4" v-for="(f,idx) in captionFields" :key="`cap-${idx}-${f.name}`">
                                <v-col class="pl-0 py-0" cols="7">
                                    <v-text-field v-model="captions[f.name]" :label="f.name" @input="forceUpdateUrl"
                                                  outlined dense hide-details></v-text-field>
                                </v-col>
                                <v-col class="py-0 pr-0" cols="5">
                                    <v-text-field v-if="f.namefontsize" v-model="captions[f.namefontsize]" :label="f.namefontsize"
                                                  type="number" suffix="%" @input="forceUpdateUrl"
                                                  outlined dense hide-details></v-text-field>
                                </v-col>
                            </v-row>

                        </div>
                        <div v-else>
                            <p class="keyinvalid"><small>Enter your actual API key in this form to see more parameters</small></p>
                        </div>
                    </v-form>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-card class="d-flex mt-3" flat tile>
            <v-card class="flex-grow-1" flat tile><div class="pa-2 fvalue" v-html="url" style="min-height: 48px;"></div></v-card>
            <v-card class="ml-auto flex-grow-0 flex-shrink-1" flat tile>
                <copy-to-clipboard :value="url"></copy-to-clipboard>
            </v-card>
        </v-card>

        <p v-if="keyvalid">
            <small><a :href="url" target="_blank" class="smfm-externallink smfm-externallink-small" @click="openWarningDialog1">Open this URL</a>
                in your browser to generate and download QR code.
            </small>
        </p>
        <div class="mt-4"></div>

        <simple-dialog header="Security warning" :no-title="true" @save="proceedToUrl(url)" ref="dialog1">
            <p>You are about to open <strong>{{ url }}</strong> in your browser. This URL will be stored in the browser history
                and other people may be able to see your API key which is a part of this URL.</p>

            <p><strong>Make sure you are not using a public computer!</strong> Alternatively, you can right click the
            link and select "Open link in incognito window".</p>

            <p>Your account has a limit of requests to the <strong>/create</strong> endpoint. Every time you access
                this URL it will increase the counter.</p>
            <template v-slot:buttons="{save}">
                <v-btn class="smfm-primary-btn smfm-lg-btn" @click="save">Proceed</v-btn>
            </template>
        </simple-dialog>

        <simple-dialog header="Security warning" title="Security warning" @save="proceedToUrl(summaryUrl)" ref="dialog2">
            <p>You are about to open <strong>{{ summaryUrl }}</strong> in your browser. This URL will be stored in the browser history
                and other people may be able to see your API key which is a part of this URL.</p>

            <p><strong>Make sure you are not using a public computer!</strong> Alternatively, you can right click the
                link and select "Open link in incognito window".</p>

            <p> Requests to the <strong>/summary</strong> endpoint are not billed.</p>

            <template v-slot:buttons="{save}">
                <v-btn class="smfm-primary-btn smfm-lg-btn" @click="save">Proceed</v-btn>
            </template>
        </simple-dialog>


    </div>
</template>

<script>
    import {mdiCheck, mdiClose} from "@mdi/js";
    import axios from "axios";
    import SimpleDialog from "../forms/SimpleDialog";
    import CopyToClipboard from "../forms/CopyToClipboard";

    export default {
        name: "AboutApiExample",
        components: {CopyToClipboard, SimpleDialog},
        data() {
            return {
                apikey: 'MYAPIKEY',
                text: 'MYTEXT',
                format: '',
                size: '',
                sizenum: '640',
                sizeunit: 'px',
                dpi: '150',
                preset: '',
                template: '',
                logo: '',
                captions: {},
                captionFields: [],
                validatedKeys: {},
                validatingKey: false,
                dialog1Shown: false,
                dialog2Shown: false,
            }
        },
        computed: {
            url() {
                let s = process.env.VUE_APP_USERAPI +
                    `/v1/create?apikey=${encodeURIComponent(this.apikey)}&text=${encodeURIComponent(this.text)}`
                if (`${this.format}`.length) s = s + '&format=' + encodeURIComponent(this.format)
                if (`${this.size}`.length) {
                    if (this.size === 'custom') {
                        s += `&size=${encodeURIComponent(this.sizenum + this.sizeunit)}&dpi=${encodeURIComponent(this.dpi)}`
                    } else {
                        s += `&size=${encodeURIComponent(this.size.split('/')[0])}&dpi=${encodeURIComponent(this.size.split('/')[1])}`
                    }
                }
                if (`${this.preset}`.length) s = s + '&preset=' + encodeURIComponent(this.preset)
                if (`${this.template}`.length) s = s + '&template=' + encodeURIComponent(this.template)
                if (`${this.logo}`.length) s = s + '&logo=' + encodeURIComponent(this.logo)
                for (let f of this.captionFields) {
                    if (this.captions[f.name] !== f.value)
                        s = s + '&' + encodeURIComponent(f.name) + '=' + encodeURIComponent(this.captions[f.name])
                    if (f.namefontsize && this.captions[f.namefontsize] !== f.valuefontsize)
                        s = s + '&' + encodeURIComponent(f.namefontsize) + '=' + encodeURIComponent(this.captions[f.namefontsize])
                }
                return s
            },
            summaryUrl() {
                return process.env.VUE_APP_USERAPI +
                    `/v1/summary?apikey=${encodeURIComponent(this.apikey)}`
            },
            formats() {
                return [{value: '', text: 'Default (PNG)'}, {value: 'svg', text: 'SVG'}, {value: 'png', text: 'PNG'}, {value: 'pdf', text: 'PDF'}, {value: 'ps', text: 'PS'}]
            },
            sizes() {
                return [{value: '', text: 'Default (640px @ 150 DPI)'},
                        {value: '2cm/150', text: '2cm @ 150 DPI'},
                        {value: '35mm/300', text: '35mm @ 300 DPI'},
                        {value: '2.5in/600', text: '2.5in @ 600 DPI'},
                        {value: 'custom', text: 'Custom...'}]
            },
            units() {
                return [
                    { value: 'px', text: this.$t('pro.download.unitpx'), precision: 1 },
                    { value: 'mm', text: this.$t('pro.download.unitmm'), precision: 1 },
                    { value: 'cm', text: this.$t('pro.download.unitcm'), precision: 10 },
                    { value: 'in', text: this.$t('pro.download.unitin'), precision: 100 },
                ]
            },
            keyMatches() {
                return `${this.apikey}`.length === 32 // TODO  match /[a-z0-9]{32}/
            },
            keyvalid() {
                return (`${this.apikey}` in this.validatedKeys) && (this.validatedKeys[`${this.apikey}`] !== false)
            },
            apikeyValidIcon() {
                return this.keyvalid ? mdiCheck : mdiClose
            },
            userLibrary() {
                return this.keyvalid ? this.validatedKeys[this.apikey]['library'] : {presets: [], templates: [], logos: []}
            },
            userPresets() {
                let res = [{value: '', text: 'No preset'}]
                for (let p of this.userLibrary['presets']) {
                    res.push({value: p.name, text: p.name})
                }
                return res
            },
            selectedPreset() {
                if (this.preset && this.keyvalid) {
                    const f = this.userLibrary['presets'].filter(p => p.name === this.preset)
                    if (f.length) return f[0]
                }
                return null
            },
            userTemplates() {
                const t = (this.selectedPreset && this.selectedPreset['defaults']['template']) ? this.selectedPreset['defaults']['template'] : 'no template'
                return [{value: '', text: `Default (${t})`},
                        ...this.userLibrary['templates'].map(p => ({value: p.name, text: p.name}))]
            },
            userLogos() {
                const t = (this.selectedPreset && this.selectedPreset['defaults']['logo']) ? this.selectedPreset['defaults']['logo'] : 'no logo'
                return [{value: '', text: `Default (${t})`},
                        ...this.userLibrary['logos'].map(p => ({value: p.name, text: p.name}))]
            },
        },
        methods: {
            validateKey(force = false) {
                if (!this.keyMatches) return
                if (!force && `${this.apikey}` in this.validatedKeys) return

                const apikey = `${this.apikey}`
                const url = process.env.VUE_APP_USERAPI +
                    `/v1/summary?apikey=${encodeURIComponent(apikey)}`
                const setKeyValidationResults = (data) => {
                    const obj = {};
                    obj[apikey] = data;
                    this.validatedKeys = {...this.validatedKeys, ...obj}
                    this.validatingKey = false
                }

                this.validatingKey = true
                axios.get(url)
                    .then(res => setKeyValidationResults(res.data))
                    .catch(() => setKeyValidationResults(false))
            },
            openWarningDialog1(e) {
                if (!this.dialog1Shown) {
                    this.$refs.dialog1.open()
                    e.preventDefault()
                    this.dialog1Shown = true
                }
            },
            openWarningDialog2(e) {
                if (!this.dialog2Shown) {
                    this.$refs.dialog2.open()
                    e.preventDefault()
                    this.dialog2Shown = true
                }
            },
            proceedToUrl(url) {
                window.open(url, '_blank')
            },
            presetUpdated() {
                if (!`${this.template}`.length) this.templateUpdated()
            },
            templateUpdated() {
                const getCaptionFields = () => {
                    let res = {}
                    if (!this.keyvalid) return res
                    const template = `${this.template}`.length ? this.template :
                        ((this.selectedPreset && this.selectedPreset['defaults']['template']) ? this.selectedPreset['defaults']['template'] : null)
                    const f = this.userLibrary['templates'].filter(p => p.name === template)
                    if (template === null || !f.length || !('defaults' in f[0])) return res
                    for (let k in f[0]['defaults']) {
                        if (k.match(/^t\./) && !k.match(/-fontsize$/))
                            res[k] = {name: k, value: f[0]['defaults'][k]}
                    }
                    for (let k in f[0]['defaults']) {
                        const m = k.match(/^(t\..*)-fontsize$/)
                        if (m && m[1] in res)
                            res[m[1]] = {...res[m[1]], namefontsize: k, valuefontsize: f[0]['defaults'][k]}
                    }
                    return res
                }
                this.captionFields = Object.values(getCaptionFields());
                for (let k of this.captionFields) {
                    this.captions[k.name] = k.value
                    if (k.namefontsize) this.captions[k.namefontsize] = k.valuefontsize
                }
            },
            forceUpdateUrl() {
                this.captions = {...this.captions}
            }
        }
    }
</script>

<style scoped>
/*! purgecss start ignore */
.fvalue {
    background: #DCF6F6;
    padding: 2px 5px;
    border-radius: 4px;
}
/deep/ .v-expansion-panels .v-expansion-panel:before {
    box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
}
.keyvalid {
    color: darkgreen;
}
.keyinvalid {
    color: red;
}
/*! purgecss end ignore */
</style>